import { useState, useEffect, useCallback, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';

type StorageKey = string | null | undefined;
type UseLocalStorageReturn<T> = [T, Dispatch<SetStateAction<T>>];

type StorageString = string | null;

const toStorageEventKey = (key: StorageKey): string | null => (key == null ? null : key);

// This lets other tabs know when localStorage changes
const dispatchStorageEvent = (key: StorageKey, newValue: StorageString) => {
  window.dispatchEvent(new StorageEvent('storage', { key: toStorageEventKey(key), newValue }));
};

const setLocalStorageItem = <T>(key: StorageKey, value: T) => {
  if (!key) return;

  const stringifiedValue = JSON.stringify(value);
  localStorage.setItem(key, stringifiedValue);
  dispatchStorageEvent(key, stringifiedValue);
};

const removeLocalStorageItem = (key: StorageKey) => {
  if (!key) return;

  localStorage.removeItem(key);
  dispatchStorageEvent(key, null);
};

const getLocalStorageItem = (key: StorageKey): StorageString => {
  if (!key) return null;

  return localStorage.getItem(key);
};

export const useLocalStorage = <T>(key: StorageKey, initialValue: T): UseLocalStorageReturn<T> => {
  const [state, setState] = useState<T>(() => {
    const storedValue = getLocalStorageItem(key);

    return storedValue ? (JSON.parse(storedValue) as T) : initialValue;
  });

  const initialRenderRef = useRef(0);

  const setLocalState = useCallback(
    (value: SetStateAction<T>) => {
      try {
        const nextState = typeof value === 'function' ? (value as (prev: T) => T)(state) : value;

        // This branch is only reachable if T includes null (or callers cast).
        if (nextState === null) {
          removeLocalStorageItem(key);
        } else {
          setLocalStorageItem(key, nextState);
        }

        setState(nextState);
      } catch (e) {
        console.warn(e);
      }
    },
    [key, state]
  );

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === toStorageEventKey(key)) {
        setState(e.newValue ? (JSON.parse(e.newValue) as T) : initialValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key, initialValue]);

  useEffect(() => {
    if (getLocalStorageItem(key) === null && initialValue !== undefined) {
      setLocalStorageItem(key, initialValue);
    }
  }, [key, initialValue]);

  useEffect(() => {
    if (key && initialRenderRef.current === 0) {
      initialRenderRef.current++;

      setState(() => {
        const storedValue = getLocalStorageItem(key);
        return storedValue ? (JSON.parse(storedValue) as T) : initialValue;
      });
    }
  }, [key, initialValue]);

  return [state, setLocalState];
};
